import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import colors from '../../assets/theme/colors';
import SimpleNotesInput from '../../components/UI/SimpleNotesInput';
import CareSourceFileUploadButton from './CareSourceFileUploadButton';
import CareSourceStatusPanel from './CareSourceStatusPanel';
import { saveTextCareSourceForProcessing } from './careSourceCaptureSubmitters';
import { saveExplicitProfileKnowledgeText } from './explicitProfileKnowledgeService';
import KnowledgeConflictConfirmationDialog from './KnowledgeConflictConfirmationDialog';
import {
  KNOWLEDGE_ITEM_TYPES,
  findLikelyKnowledgeConflict,
  loadKnowledgeItems,
} from '../knowledgeItems';
import { getCurrentKnowledgeForProfilePlacement } from '../profileContract';
import { PROFILE_PLACEMENT_MODES } from '../profileContract/profilePlacementMetadata';
import { CARE_SOURCE_CONTEXTS } from './careSourceMetadata';
import { getCareSourceDisplayStatus } from './careSourceStatusModel';
import { shouldShowPendingProfileSourceForChapter } from './profileSourceActivity';

const getDraftText = (value = {}) => String(value?.text || '').trim();

const shouldShowCareKnowledgeSourceActivity = (source = {}) => {
  const status = getCareSourceDisplayStatus(source).label;
  return status === 'Processing' || status === 'Reading text' || status === 'Failed';
};

const getUploadActionButtonSx = (accent) => ({
  minHeight: 58,
  borderRadius: '13px',
  flexDirection: 'column',
  gap: 0.25,
  borderWidth: '1.2px',
  borderColor: alpha(colors.semantic.line2, 0.72),
  color: alpha(colors.semantic.ink, 0.82),
  bgcolor: '#FFFFFF',
  fontSize: '0.72rem',
  fontWeight: 750,
  boxShadow: 'none',
  '& .MuiButton-startIcon': {
    m: 0,
    '& svg': {
      fontSize: 18,
    },
  },
  '&:hover': {
    borderColor: alpha(accent?.primary || colors.semantic.sageDeep, 0.44),
    bgcolor: alpha(accent?.primary || colors.semantic.sageDeep, 0.04),
  },
});

const getFieldSx = (accent) => ({
  '& .MuiOutlinedInput-root': {
    minHeight: 142,
    alignItems: 'flex-start',
    borderRadius: '18px',
    bgcolor: '#FFFFFF',
    fontSize: '0.9rem',
    color: colors.semantic.ink,
    '& fieldset': {
      borderColor: colors.semantic.line,
    },
    '&:hover fieldset': {
      borderColor: colors.semantic.line2,
    },
    '&.Mui-focused fieldset': {
      borderColor: accent?.primary || colors.semantic.sageDeep,
    },
  },
  '& .MuiOutlinedInput-input': {
    pt: 1.2,
    px: 1,
    lineHeight: 1.4,
  },
  '& textarea::placeholder': {
    color: alpha(colors.semantic.ink, 0.5),
    opacity: 1,
  },
});

const CareKnowledgeCaptureCard = ({
  childId = '',
  childName = 'this profile',
  accent,
  title = 'Add care info',
  placeholder = 'Type, use your phone keyboard mic, or paste care info...',
  helperText = 'Becomes a fact below, tagged with today as its source.',
  profileChapterId = '',
  profileChapterName = '',
  profileField = '',
  profilePlacementMode = '',
  onExplicitProfileTextSaved,
  onExplicitProfileSourceSaved,
}) => {
  const [draft, setDraft] = useState({ text: '' });
  const [clearToken, setClearToken] = useState(0);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageSeverity, setMessageSeverity] = useState('success');
  const [errorMessage, setErrorMessage] = useState('');
  const [pendingConflict, setPendingConflict] = useState(null);
  const draftText = getDraftText(draft);
  const activeAccent = accent || {};
  const accentPrimary = activeAccent.primary || colors.brand.deep;
  const accentInk = activeAccent.ink || accentPrimary;
  const profileActivityFilter = useCallback((source) => {
    if (shouldShowCareKnowledgeSourceActivity(source)) return true;
    return shouldShowPendingProfileSourceForChapter(source, profileChapterId);
  }, [profileChapterId]);

  const handleDraftChange = (nextDraft) => {
    setDraft(nextDraft);
    if (message) setMessage('');
    if (errorMessage) setErrorMessage('');
  };

  const completeSave = (result, isExplicitProfileAdd) => {
    const successSeverity = result?.duplicate ? 'info' : 'success';
    const successMessage = isExplicitProfileAdd
      ? `Added to ${profileChapterName || title}`
      : (result?.message || 'Care info saved. We are organizing it in the background.');

    setMessageSeverity(successSeverity);
    setMessage(successMessage);
    setDraft({ text: '' });
    setClearToken((current) => current + 1);
    if (isExplicitProfileAdd) {
      onExplicitProfileTextSaved?.({
        message: successMessage,
        severity: successSeverity,
        ...(result?.profileOrganizationMode === 'complex' ? {
          keepOpenForOrganization: true,
          sourceId: result.sourceNoteId || result.id || '',
        } : {}),
      });
    }
  };

  const saveExplicitText = async (rawText, supersededKnowledgeItemId = '') => {
    const result = await saveExplicitProfileKnowledgeText({
      childId,
      rawText,
      profileChapterId,
      profileField,
      profilePlacementMode,
      supersededKnowledgeItemId,
    });
    completeSave(result, true);
  };

  const handleExplicitProfileImageSaved = (result = {}) => {
    if (
      profilePlacementMode !== PROFILE_PLACEMENT_MODES.EXPLICIT_CAREGIVER
      || !profileChapterId
    ) return;
    onExplicitProfileSourceSaved?.({
      message: `Photo saved. Organizing for ${profileChapterName || title}…`,
      severity: 'success',
      keepOpenForOrganization: true,
      sourceId: result.sourceNoteId || result.id || '',
    });
  };

  const handleSaveTextSource = async () => {
    if (saving) return;

    if (!draftText) {
      setErrorMessage('Type, paste, or dictate care info first.');
      return;
    }

    if (!childId) {
      setErrorMessage('Choose a profile before adding care info.');
      return;
    }

    setSaving(true);
    setMessage('');
    setErrorMessage('');

    try {
      const isExplicitProfileAdd = profilePlacementMode === PROFILE_PLACEMENT_MODES.EXPLICIT_CAREGIVER
        && Boolean(profileChapterId);
      if (isExplicitProfileAdd) {
        const currentItems = await loadKnowledgeItems(childId);
        const sameDomainItems = getCurrentKnowledgeForProfilePlacement(currentItems, profileChapterId);
        const conflict = findLikelyKnowledgeConflict({
          knowledgeType: KNOWLEDGE_ITEM_TYPES.PROFILE,
          profileChapterId,
          profileField,
          title: draftText,
          summary: draftText,
        }, sameDomainItems, { assumeSameDomain: true });
        if (conflict) {
          setPendingConflict({ conflict, draft: { content: draftText }, draftText });
          return;
        }
        await saveExplicitText(draftText);
      } else {
        const result = await saveTextCareSourceForProcessing({
          childId,
          rawText: draftText,
          sourceType: 'care_knowledge',
          sourceName: 'Typed care info',
          inputMode: 'care_knowledge_text',
          sourceContext: CARE_SOURCE_CONTEXTS.KNOWLEDGE,
        });
        completeSave(result, false);
      }
    } catch (error) {
      setErrorMessage(error?.message || 'We could not save this care info right now.');
    } finally {
      setSaving(false);
    }
  };

  const handleConflictResolution = async (decision = '') => {
    if (!pendingConflict || !['keep_both', 'supersede'].includes(decision)) return;
    const conflict = pendingConflict;
    setPendingConflict(null);
    setSaving(true);
    setErrorMessage('');
    try {
      await saveExplicitText(
        conflict.draftText,
        decision === 'supersede' ? conflict.conflict.id : ''
      );
    } catch (error) {
      setErrorMessage(error?.message || 'We could not save this care info right now.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 1.15 },
        borderRadius: '18px',
        border: `1px solid ${alpha(accentPrimary, 0.18)}`,
        bgcolor: '#FFFFFF',
        boxShadow: `0 8px 22px ${colors.landing.shadowSoft}`,
      }}
    >
      <Stack spacing={0.9}>
        <Stack direction="row" spacing={0.8} alignItems="center">
          <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: colors.semantic.ink, lineHeight: 1.12 }}>
            {title}
          </Typography>
          <Box
            sx={{
              px: 0.65,
              py: 0.25,
              borderRadius: '999px',
              bgcolor: alpha(accentPrimary, 0.1),
              color: accentInk,
              fontSize: '0.72rem',
              fontWeight: 850,
              lineHeight: 1.2,
            }}
          >
            {childName}
          </Box>
        </Stack>

        <SimpleNotesInput
          value={draft}
          clearData={clearToken}
          onDataChange={handleDraftChange}
          placeholder={placeholder}
          hintText=""
          minRows={4}
          maxRows={7}
          textFieldSx={getFieldSx(activeAccent)}
        />

        <Divider sx={{ my: 0.1, color: alpha(colors.semantic.ink, 0.5), '&::before, &::after': { borderColor: colors.semantic.line } }}>
          <Typography sx={{ px: 1, fontSize: '0.78rem', fontWeight: 850, color: alpha(colors.semantic.ink, 0.58) }}>
            or
          </Typography>
        </Divider>

        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            '& > *': {
              flex: 1,
              minWidth: 0,
            },
          }}
        >
          <CareSourceFileUploadButton
            childId={childId}
            sourceContext={CARE_SOURCE_CONTEXTS.KNOWLEDGE}
            sourceType="care_knowledge_document"
            label="File"
            icon={<DescriptionOutlinedIcon />}
            acceptedFiles=".pdf,.txt,.csv,.tsv,.xls,.xlsx,.docx"
            variantStyle="document"
            buttonSx={getUploadActionButtonSx(activeAccent)}
            profileChapterId={profileChapterId}
            profileField={profileField}
            profilePlacementMode={profilePlacementMode}
          />
          <CareSourceFileUploadButton
            childId={childId}
            sourceContext={CARE_SOURCE_CONTEXTS.KNOWLEDGE}
            sourceType="care_knowledge_image"
            label="Photo or screenshot"
            icon={<ImageOutlinedIcon />}
            acceptedFiles="image/*"
            variantStyle="document"
            buttonSx={getUploadActionButtonSx(activeAccent)}
            profileChapterId={profileChapterId}
            profileField={profileField}
            profilePlacementMode={profilePlacementMode}
            onSaved={handleExplicitProfileImageSaved}
          />
        </Stack>

        <Button
          variant="contained"
          disabled={saving || !draftText || !childId}
          onClick={handleSaveTextSource}
          endIcon={saving ? null : <ChevronRightRoundedIcon />}
          sx={{
            minHeight: 48,
            width: '100%',
            borderRadius: '999px',
            mt: 0.15,
            px: 1.5,
            fontSize: '0.9rem',
            fontWeight: 900,
            textTransform: 'none',
            boxShadow: 'none',
            bgcolor: accentPrimary,
            color: '#fff',
            '& .MuiButton-endIcon': {
              ml: 0.35,
              '& svg': {
                fontSize: 22,
              },
            },
            '&.Mui-disabled': {
              bgcolor: alpha(colors.semantic.ink, 0.12),
              color: alpha(colors.semantic.ink, 0.34),
            },
            '&:hover': {
              bgcolor: accentInk,
              boxShadow: 'none',
            },
          }}
        >
          {saving ? 'Saving...' : 'Continue'}
        </Button>

        <Stack
          direction="row"
          spacing={0.65}
          alignItems="center"
          sx={{
            px: 0.9,
            py: 0.65,
            borderRadius: '12px',
            bgcolor: alpha(accentPrimary, 0.08),
            color: accentInk,
          }}
        >
          <AutoAwesomeOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
          <Typography sx={{ textAlign: 'left', fontSize: '0.76rem', fontWeight: 850, lineHeight: 1.35 }}>
            {helperText}
          </Typography>
        </Stack>

        {message ? (
          <Alert severity={messageSeverity} sx={{ py: 0.3 }}>
            {message}
          </Alert>
        ) : null}
        {errorMessage ? (
          <Alert severity="warning" sx={{ py: 0.3 }}>
            {errorMessage}
          </Alert>
        ) : null}

        <CareSourceStatusPanel
          childId={childId}
          sourceContext={CARE_SOURCE_CONTEXTS.KNOWLEDGE}
          title="Care Knowledge source activity"
          activityFilter={profileChapterId ? profileActivityFilter : shouldShowCareKnowledgeSourceActivity}
          profileReviewMode={Boolean(profileChapterId)}
        />
      </Stack>
      <KnowledgeConflictConfirmationDialog
        conflict={pendingConflict}
        onResolve={handleConflictResolution}
      />
    </Box>
  );
};

export default CareKnowledgeCaptureCard;
